import React, {useState, useEffect, useMemo} from 'react';
import {Text} from '@/ui-kits';
import {formatConsultationDate, formatName} from '@/utils/common_functions';

interface PatientHeaderProps {
  patientName: string;
  caseId: string;
  category: string;
  receivedOn: string; // Expected format: ISO string or parseable date string
  deadlineHours?: number; // Optional: default 24 hours
  reviewAt?: string;
}

interface TimeRemaining {
  hours: string;
  minutes: string;
  seconds: string;
  isExpired: boolean;
}

const PatientHeader: React.FC<PatientHeaderProps> = props => {
  const {patientName, caseId, category, receivedOn, deadlineHours, reviewAt} = props;
  const [timeRemaining, setTimeRemaining] = useState<TimeRemaining>({
    hours: '00',
    minutes: '00',
    seconds: '00',
    isExpired: false,
  });

  const deadline = new Date(new Date(receivedOn).getTime() + (deadlineHours || 48) * 60 * 60 * 1000).toString();

  const infoItem = useMemo(
    () => [
      ['PATIENT NAME', formatName(patientName)],
      ['CASE ID', '#' + caseId],
      ['CATEGORY', category],
      [reviewAt ? 'SUBMITTED ON' : 'RECEIVED ON', reviewAt || receivedOn],
    ],
    [patientName, caseId, category, receivedOn],
  );

  // memo to calculate the time remaining styles, bg colors and text colors
  const {timerBgColor, timerTextColor} = useMemo(() => {
    if (timeRemaining.isExpired) {
      return {
        timerBgColor: 'bg-red-100',
        timerTextColor: 'text-red-600',
      };
    }
    if (Number.parseInt(timeRemaining.hours) < 2) {
      return {
        timerBgColor: 'bg-orange-100',
        timerTextColor: 'text-orange-600',
      };
    }
    return {
      timerBgColor: 'bg-[#C9EAFB]',
      timerTextColor: 'text-ui_blue',
    };
  }, [timeRemaining]);

  // side effect to calculate the time remaining
  useEffect(() => {
    if (reviewAt) {
      return;
    }
    const calculateTimeRemaining = () => {
      const receivedDate = new Date(receivedOn);
      const deadlineDate = new Date(receivedDate.getTime() + (deadlineHours || 48) * 60 * 60 * 1000);
      const now = new Date();

      const diffMs = deadlineDate.getTime() - now.getTime();

      if (diffMs <= 0) {
        setTimeRemaining({
          hours: '00',
          minutes: '00',
          seconds: '00',
          isExpired: true,
        });
        return;
      }

      const hours = Math.floor(diffMs / (1000 * 60 * 60));
      const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);

      setTimeRemaining({
        hours: hours.toString().padStart(2, '0'),
        minutes: minutes.toString().padStart(2, '0'),
        seconds: seconds.toString().padStart(2, '0'),
        isExpired: false,
      });
    };

    // Calculate immediately
    calculateTimeRemaining();

    // Update every second
    const interval = setInterval(calculateTimeRemaining, 1000);

    return () => clearInterval(interval);
  }, [receivedOn, deadlineHours]);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-[#447DA8] border-l-[5px] p-4 sm:p-4 gap-4 sm:gap-2">
      {/* Patient Info - Stacked on mobile, horizontal on desktop */}
      {/* <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-[1.3fr_0.7fr_2fr_2fr_2fr] items-center gap-3 xl:gap-5"> */}
      <div className="flex flex-col gap-2 md:flex-row sm:gap-3 sm:items-start justify-between">
        {infoItem.map(item => (
          <div key={item[0]} className="min-w-0">
            <Text variant="small" className="text-ui_input_border_dark mb-1 !text-xs sm:!text-sm">
              {item[0]}
            </Text>
            <Text variant="subtitle" className="text-ui_blue !text-sm sm:!text-base">
              {item[1]}
            </Text>
          </div>
        ))}
        {!reviewAt && (
        <div className="flex flex-col gap-2 sm:gap-3 sm:items-start w-fit">
          {!timeRemaining.isExpired ? (
            <div
              className={`${timerBgColor} ${timerTextColor} w-fit sm:w-[190px] justify-start sm:justify-center px-3 sm:px-6 py-2 sm:py-3 rounded-lg flex items-center gap-1.5 sm:gap-2`}>
              <div className="text-center">
                <div className="text-xl sm:text-2xl font-FigtreeBold tabular-nums">{timeRemaining.hours} :</div>
                <div className="text-[10px] font-FigtreeMedium">HRS</div>
              </div>
              <div className="text-center">
                <div className="text-xl sm:text-2xl font-FigtreeBold tabular-nums">{timeRemaining.minutes} :</div>
                <div className="text-[10px] font-FigtreeMedium">MIN</div>
              </div>
              <div className="text-center">
                <div className="text-xl sm:text-2xl font-FigtreeBold tabular-nums">{timeRemaining.seconds}</div>
                <div className="text-[10px] font-FigtreeMedium">SEC</div>
              </div>
            </div>
          ) : (
            <div
              className={`flex flex-col`}>
              <Text variant="small" className="text-ui_input_border_dark mb-1 !text-xs sm:!text-sm">
                DEADLINE
              </Text>
              <Text variant="subtitle" className="text-red-600 !text-sm sm:!text-base">
                {formatConsultationDate(deadline)}
              </Text>
            </div>
          )}
        </div>
      )}
      </div>
    </div>
  );
};

export default PatientHeader;
